// ./src/utils/api/CarMonthlyExpenseService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { ExpenseKind } from "./AppEssentialsService";
import { CarMonthlySummary } from "./CarMonthlySummaryService";

// GraphQL queries
const CAR_MONTHLY_EXPENSE_LIST_QUERY = gql`
  query CarMonthlyExpenseList(
    $filter: CarMonthlyExpenseFilter
    $params: PaginationAndSorting
  ) {
    carMonthlyExpenseList(filter: $filter, params: $params) {
      code
      data {
        carMonthlySummaryId
        expenseKindId
        recordsCount
        amount
        carMonthlySummary {
          id
          carId
          year
          month
        }
        expenseKind {
          id
          code
          canSchedule
          isItMaintenance
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_MONTHLY_EXPENSE_GET_QUERY = gql`
  query CarMonthlyExpenseGet($carMonthlySummaryId: ID, $expenseKindId: Int) {
    carMonthlyExpenseGet(
      carMonthlySummaryId: $carMonthlySummaryId
      expenseKindId: $expenseKindId
    ) {
      code
      data {
        carMonthlySummaryId
        expenseKindId
        recordsCount
        amount
        carMonthlySummary {
          id
          carId
          year
          month
        }
        expenseKind {
          id
          code
          canSchedule
          isItMaintenance
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface CarMonthlyExpense {
  carMonthlySummaryId?: string | undefined;
  expenseKindId?: number | undefined;
  recordsCount?: number | undefined;
  amount?: number | undefined;
  carMonthlySummary?: CarMonthlySummary | undefined;
  expenseKind?: ExpenseKind | undefined;
}

export interface CarMonthlyExpenseFilter {
  carMonthlySummaryId?: string[] | null | undefined;
  expenseKindId?: number[] | null | undefined;
}

export interface CarMonthlyExpenseWhereInput {
  carMonthlySummaryId?: string | undefined;
  expenseKindId?: number | undefined;
}

/**
 * CarMonthlyExpense service that handles car monthly expense-related API calls.
 * This is a read-only service for viewing aggregated monthly expense data by expense kind.
 * Supports: list, get (composite key)
 */
export class CarMonthlyExpenseService {
  /**
   * Get a list of car monthly expenses with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: CarMonthlyExpenseFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      CAR_MONTHLY_EXPENSE_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single car monthly expense by composite key (carMonthlySummaryId + expenseKindId)
   */
  static async get(
    carMonthlySummaryId: string,
    expenseKindId: number
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      CAR_MONTHLY_EXPENSE_GET_QUERY,
      {
        carMonthlySummaryId,
        expenseKindId,
      }
    );
    return result;
  }
}
